'use client';

import { useEffect, useState } from 'react';

export type ActivityType = 'viewed_car' | 'viewed_part' | 'used_diagnosis' | 'viewed_service';

export interface UserActivity {
    type: ActivityType;
    id: string;
    name: string;
    metadata?: Record<string, any>;
    timestamp: string;
}

const STORAGE_KEY = 'siyarati_behavior_history';
const MAX_HISTORY_LENGTH = 15; // Keep payload small for AI prompt

export function useBehaviorTracker() {
    const [history, setHistory] = useState<UserActivity[]>([]);

    useEffect(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                setHistory(JSON.parse(stored));
            }
        } catch (e) {
            console.error('Failed to parse behavior history', e);
        }
    }, []);

    const logActivity = (type: ActivityType, id: string, name: string, metadata?: Record<string, any>) => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            let currentHistory: UserActivity[] = stored ? JSON.parse(stored) : [];

            // Remove duplicate if same item viewed again
            currentHistory = currentHistory.filter(item => !(item.type === type && item.id === id));

            const newActivity: UserActivity = {
                type,
                id,
                name,
                metadata,
                timestamp: new Date().toISOString(),
            };

            currentHistory.unshift(newActivity);

            if (currentHistory.length > MAX_HISTORY_LENGTH) {
                currentHistory = currentHistory.slice(0, MAX_HISTORY_LENGTH);
            }

            localStorage.setItem(STORAGE_KEY, JSON.stringify(currentHistory));
            setHistory(currentHistory);
        } catch (e) {
            console.error('Failed to log behavior metric', e);
        }
    };

    const getHistoryString = () => {
        if (history.length === 0) return null;
        return JSON.stringify(history.map(h => ({
            action: h.type,
            item: h.name,
            details: h.metadata || {},
            timeAgo: Math.round((new Date().getTime() - new Date(h.timestamp).getTime()) / 60000) + ' minutes ago'
        })));
    };

    const clearHistory = () => {
        localStorage.removeItem(STORAGE_KEY);
        setHistory([]);
    };

    return {
        history,
        logActivity,
        getHistoryString,
        clearHistory
    };
}
